import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { Euler, useLoader, Vector3 } from "@react-three/fiber";
import { useRef, useState } from "react";

import * as THREE from 'three';
import mujocoQuatToEuler from "../math/mujocoQuat";
import { FingerSegment } from "../base/FingerSegment";
import HandConfiguration from "../types/HandConfiguration";

type LeftHandProps = {
    pos?: Vector3,
    euler?: Euler
}

export const LeftHand = (props: LeftHandProps) => {
    const initialConfig: HandConfiguration = {
        thumb: {
            abd: 0,
            mcp: 1.35,
            pip: 0.21,
            dip: 0.21,
            abd2: 0.01
        },
        index: {
            abd: -0.15,
            mcp: 1.6,
            pip: 1,
            dip: 0.1
        },
        middle: {
            abd: 0,
            mcp: 1.4,
            pip: 0.3,
            dip: 0.3
        },
        ring: {
            abd: 0,
            mcp: 1.27,
            pip: 0.27,
            dip: 0.16
        },
        pinky: {
            abd: 0,
            mcp: 1.26,
            pip: 0.55,
            dip: 0.55
        }
    };
    const [config, _] = useState(initialConfig);

    const palm = useLoader(STLLoader, "/stl/hand/palm_mirrored.stl");

    const thumb0 = useLoader(STLLoader, "/stl/hand/thumb0_collision_left.stl");
    const thumb1 = useLoader(STLLoader, "/stl/hand/thumb1_collision_left.stl");
    const thumb2 = useLoader(STLLoader, "/stl/hand/thumb2_collision_left.stl");
    const thumb3 = useLoader(STLLoader, "/stl/hand/thumb3_collision_left.stl");

    const index0 = useLoader(STLLoader, "/stl/hand/index0_collision_left.stl");
    const index1 = useLoader(STLLoader, "/stl/hand/index1_collision_left.stl");
    const index2 = useLoader(STLLoader, "/stl/hand/index2_collision_left.stl");
    const index3 = useLoader(STLLoader, "/stl/hand/index3_collision_left.stl");

    const middle0 = useLoader(STLLoader, "/stl/hand/middle0_collision_left.stl");
    const middle1 = useLoader(STLLoader, "/stl/hand/middle1_collision_left.stl");
    const middle2 = useLoader(STLLoader, "/stl/hand/middle2_collision_left.stl");
    const middle3 = useLoader(STLLoader, "/stl/hand/middle3_collision_left.stl");

    const ring0 = useLoader(STLLoader, "/stl/hand/ring0_collision_left.stl");
    const ring1 = useLoader(STLLoader, "/stl/hand/ring1_collision_left.stl");
    const ring2 = useLoader(STLLoader, "/stl/hand/ring2_collision_left.stl");
    const ring3 = useLoader(STLLoader, "/stl/hand/ring3_collision_left.stl");

    const pinky0 = useLoader(STLLoader, "/stl/hand/pinky0_collision_left.stl");
    const pinky1 = useLoader(STLLoader, "/stl/hand/pinky1_collision_left.stl");
    const pinky2 = useLoader(STLLoader, "/stl/hand/pinky2_collision_left.stl");
    const pinky3 = useLoader(STLLoader, "/stl/hand/pinky3_collision_left.stl");

    const handColor: [number, number, number] = [0.8, 0.6, 0.4];

    const ref = useRef();

    return (
        <>
            <group position={props.pos} rotation={props.euler}>
                {/* The palm. */}
                <mesh ref={ref} position={[-0.025, 0, 0]}>
                    <primitive object={palm} attach="geometry" />
                    <meshStandardMaterial color={handColor} />

                    {/* The thumb. */}
                    <FingerSegment
                        color={handColor}
                        position={[0, -0.026, -0.01]}
                        rotation={mujocoQuatToEuler([0, 0, 1, 1])}
                        geometry={thumb0}
                        joint={{
                            axis: new THREE.Vector3(0, 1, 0),
                            position: new THREE.Vector3(0, 0, 0),
                            value: config.thumb.abd
                        }}
                        joint2={{
                            axis: new THREE.Vector3(1, 0, 0),
                            position: new THREE.Vector3(0, 0, 0),
                            value: config.thumb.abd2!
                        }}>
                        <FingerSegment
                            color={handColor}
                            position={[0.02, 0, 0]}
                            geometry={thumb1}
                            joint={{
                                axis: new THREE.Vector3(0, 0, -1),
                                position: new THREE.Vector3(0, 0, 0),
                                value: config.thumb.mcp
                            }}>
                            <FingerSegment
                                color={handColor}
                                position={[0.03, 0, 0]}
                                geometry={thumb2}
                                joint={{
                                    axis: new THREE.Vector3(0, 0, -1),
                                    position: new THREE.Vector3(0, 0, 0),
                                    value: config.thumb.pip
                                }}>
                                <FingerSegment
                                    color={handColor}
                                    position={[0.03, 0, 0]}
                                    geometry={thumb3}
                                    joint={{
                                        axis: new THREE.Vector3(0, 0, -1),
                                        position: new THREE.Vector3(0, 0, 0),
                                        value: config.thumb.dip
                                    }}></FingerSegment>
                            </FingerSegment>
                        </FingerSegment>
                    </FingerSegment>

                    {/* The index finger. */}
                    <FingerSegment
                        color={handColor}
                        position={[-0.01, -0.065, 0]}
                        rotation={mujocoQuatToEuler([1, 0, 0, -0.1])}
                        geometry={index0}
                        joint={{
                            axis: new THREE.Vector3(0, 0, 1),
                            position: new THREE.Vector3(0, 0, 0),
                            value: config.index.abd
                        }}>
                        <FingerSegment
                            color={handColor}
                            position={[0, -0.018, 0]}
                            geometry={index1}
                            joint={{
                                axis: new THREE.Vector3(1, 0, 0),
                                position: new THREE.Vector3(0, 0, 0),
                                value: config.index.mcp
                            }}>
                            <FingerSegment
                                color={handColor}
                                position={[0, -0.043, 0]}
                                geometry={index2}
                                joint={{
                                    axis: new THREE.Vector3(1, 0, 0),
                                    position: new THREE.Vector3(0, 0, 0),
                                    value: config.index.pip
                                }}>
                                <FingerSegment
                                    color={handColor}
                                    position={[0, -0.02, 0]}
                                    geometry={index3}
                                    joint={{
                                        axis: new THREE.Vector3(1, 0, 0),
                                        position: new THREE.Vector3(0, 0, 0),
                                        value: config.index.dip
                                    }}></FingerSegment>
                            </FingerSegment>
                        </FingerSegment>
                    </FingerSegment>

                    {/* The middle finger. */}
                    <FingerSegment
                        color={handColor}
                        position={[0.0128, -0.0779, 0.0054]}
                        rotation={mujocoQuatToEuler([-3, 0.043, 0, 0])}
                        geometry={middle0}
                        joint={{
                            axis: new THREE.Vector3(0, 0, -1),
                            position: new THREE.Vector3(0, 0, 0),
                            value: config.middle.abd
                        }}>
                        <FingerSegment
                            color={handColor}
                            position={[0, -0.018, 0]}
                            geometry={middle1}
                            joint={{
                                axis: new THREE.Vector3(1, 0, 0),
                                position: new THREE.Vector3(0, 0, 0),
                                value: config.middle.mcp
                            }}>
                            <FingerSegment
                                color={handColor}
                                position={[0.0011, -0.0435, 0.0005]}
                                geometry={middle2}
                                joint={{
                                    axis: new THREE.Vector3(1, 0, 0),
                                    position: new THREE.Vector3(0, 0, 0),
                                    value: config.middle.pip
                                }}>
                                <FingerSegment
                                    color={handColor}
                                    position={[0, -0.024, 0]}
                                    geometry={middle3}
                                    joint={{
                                        axis: new THREE.Vector3(1, 0, 0),
                                        position: new THREE.Vector3(0, 0, 0),
                                        value: config.middle.dip
                                    }}></FingerSegment>
                            </FingerSegment>
                        </FingerSegment>
                    </FingerSegment>

                    {/* The ring finger. */}
                    <FingerSegment
                        color={handColor}
                        position={[0.0355, -0.0667, 0.0015]}
                        rotation={mujocoQuatToEuler([1, 0, 0, 0])}
                        geometry={ring0}
                        joint={{
                            axis: new THREE.Vector3(0, 0, -1),
                            position: new THREE.Vector3(0, 0, 0),
                            value: config.ring.abd
                        }}>
                        <FingerSegment
                            color={handColor}
                            position={[0, -0.018, 0]}
                            geometry={ring1}
                            joint={{
                                axis: new THREE.Vector3(1, 0, 0),
                                position: new THREE.Vector3(0, 0, 0),
                                value: config.ring.mcp
                            }}>
                            <FingerSegment
                                color={handColor}
                                position={[0.0011, -0.0435, 0.0005]}
                                geometry={ring2}
                                joint={{
                                    axis: new THREE.Vector3(1, 0, 0),
                                    position: new THREE.Vector3(0, 0, 0),
                                    value: config.ring.pip
                                }}>
                                <FingerSegment
                                    color={handColor}
                                    position={[0, -0.024, 0]}
                                    geometry={ring3}
                                    joint={{
                                        axis: new THREE.Vector3(1, 0, 0),
                                        position: new THREE.Vector3(0, 0, 0),
                                        value: config.ring.dip
                                    }}></FingerSegment>
                            </FingerSegment>
                        </FingerSegment>
                    </FingerSegment>

                    {/* The pinky. */}
                    <FingerSegment
                        color={handColor}
                        position={[0.056, -0.055, -0.0056]}
                        rotation={mujocoQuatToEuler([1, 0, 0.06, 0.15])}
                        geometry={pinky0}
                        joint={{
                            axis: new THREE.Vector3(0, 0, -1),
                            position: new THREE.Vector3(0, 0, 0),
                            value: config.pinky.abd
                        }}>
                        <FingerSegment
                            color={handColor}
                            position={[0, -0.0179, 0]}
                            geometry={pinky1}
                            joint={{
                                axis: new THREE.Vector3(1, 0, 0),
                                position: new THREE.Vector3(0, 0, 0),
                                value: config.pinky.mcp
                            }}>
                            <FingerSegment
                                color={handColor}
                                position={[0.00057, -0.033, 0.0005]}
                                geometry={pinky2}
                                joint={{
                                    axis: new THREE.Vector3(1, 0, 0),
                                    position: new THREE.Vector3(0, 0, 0),
                                    value: config.pinky.pip
                                }}>
                                <FingerSegment
                                    color={handColor}
                                    position={[0, -0.017, 0]}
                                    geometry={pinky3}
                                    joint={{
                                        axis: new THREE.Vector3(1, 0, 0),
                                        position: new THREE.Vector3(0, 0, 0),
                                        value: config.pinky.dip
                                    }}></FingerSegment>
                            </FingerSegment>
                        </FingerSegment>
                    </FingerSegment>
                </mesh>
            </group>

        </>
    );
};
